import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Printer, X, MapPin, Hexagon, Route, Ruler, Layers, Waves } from 'lucide-react';
import * as Cesium from 'cesium';
// Note: the Print entry point lives in the bottom toolbar (CanvasBottomChrome);
// this component is controlled via isOpen/onClose.
import { PAPER_SIZES, getPaperSize, computeSheetLayout, sheetDimsMm, } from '../utils/printPaper';
import { exportPrintPdf, isSafari, exportPixelCount, SAFARI_MAX_CANVAS_PX, } from '../utils/printExport';
import { applyTerrainModeToAllVectorLayers } from '../utils/vectorLayerTerrainSync';
import { useUnitPreferences } from '../hooks/useUnitPreferences';
import PrintTitleBlock from './PrintTitleBlock';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
import { DEM_SOURCE_CITATIONS, resolveDemSourceType } from '../data/dataSourceRegistry';
import { buildSourceCredit } from '../utils/sourceCredit';
import PrintMapOverlay from './PrintMapOverlay';
import PrintLegend from './PrintLegend';
import { LabelMapOverlay } from './LabelTool';
import { usePrintLegend } from '../hooks/usePrintLegend';
import { presentFeatures, buildLegendRows } from '../utils/legendData';
// Same type icons the control-panel folder tree uses, so the legend editor
// reads as "what it is" rather than a bare colour chip.
const KIND_ICON = {
    placemark: MapPin,
    polygon: Hexagon,
    path: Route,
    measurement: Ruler,
    contour: Layers,
    stream: Waves,
};
const PANEL_W = 300;
const DESK = '#e6e6e2';
const PAPER = '#ffffff';
const rectStyle = (r) => ({
    position: 'absolute',
    left: r.x,
    top: r.y,
    width: r.width,
    height: r.height,
});
const shiftX = (r, dx) => ({ ...r, x: r.x + dx });
const insideRect = (x, y, r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height;
// Frame-scopes the legend: keeps a feature only when one of its representative
// points projects to a screen position inside the print map window. Features
// with no geometry are kept (we can't place them, so we don't hide them).
// Runs against the FULL-width map window (legend-less layout) so the result is
// independent of whether the legend column is shown — no feedback loop.
const filterFeaturesToFrame = (viewer, features, rect) => {
    if (!rect || !viewer || viewer.isDestroyed())
        return features;
    const scene = viewer.scene;
    const scratch = new Cesium.Cartesian3();
    return features.filter((f) => {
        const pts = f.positions;
        if (!pts || pts.length === 0)
            return true;
        for (const p of pts) {
            Cesium.Cartesian3.fromDegrees(p.lng, p.lat, 0, undefined, scratch);
            const win = Cesium.SceneTransforms.worldToWindowCoordinates(scene, scratch);
            if (win && insideRect(win.x, win.y, rect))
                return true;
        }
        return false;
    });
};
const Segmented = ({ options, value, onChange }) => (_jsx("div", { className: "mt-1 flex w-full p-0.5 rounded-full", style: { backgroundColor: 'var(--charcoal)', border: '1px solid var(--pewter)' }, children: options.map((o) => {
        const active = o.value === value;
        return (_jsx("button", { onClick: () => onChange(o.value), className: "flex-1 px-3 py-1.5 text-sm rounded-full transition-colors", style: {
                backgroundColor: active ? 'var(--sand)' : 'transparent',
                color: active ? 'var(--forest)' : 'var(--sand)',
            }, children: o.label }, o.value));
    }) }));
const SheetOverlay = ({ layout, containerW, containerH, viewer, title, subtitle, description, scaleSystem, legend, sourceCredit, onDescriptionFit, labels, }) => {
    const { sheet, mapWindow, titleBlock } = layout;
    const sheetRight = sheet.x + sheet.width;
    const sheetBottom = sheet.y + sheet.height;
    // Gray desk: everything outside the sheet (4 rects) — opaque, so the live map
    // and all app chrome behind it are fully hidden.
    const desk = [
        { x: 0, y: 0, width: containerW, height: sheet.y },
        { x: 0, y: sheetBottom, width: containerW, height: containerH - sheetBottom },
        { x: 0, y: sheet.y, width: sheet.x, height: sheet.height },
        { x: sheetRight, y: sheet.y, width: containerW - sheetRight, height: sheet.height },
    ];
    // White paper framing the map window (4 rects). The window itself is left as a
    // hole so the live map shows through and stays interactive.
    const winRight = mapWindow.x + mapWindow.width;
    const winBottom = mapWindow.y + mapWindow.height;
    const paperFrame = [
        { x: sheet.x, y: sheet.y, width: sheet.width, height: mapWindow.y - sheet.y },
        { x: sheet.x, y: winBottom, width: sheet.width, height: sheetBottom - winBottom },
        { x: sheet.x, y: mapWindow.y, width: mapWindow.x - sheet.x, height: mapWindow.height },
        { x: winRight, y: mapWindow.y, width: sheetRight - winRight, height: mapWindow.height },
    ];
    return (_jsxs(_Fragment, { children: [desk.map((r, i) => (_jsx("div", { style: { ...rectStyle(r), background: DESK, pointerEvents: 'auto' } }, `d${i}`))), _jsx("div", { style: {
                    ...rectStyle(sheet),
                    boxShadow: '0 10px 34px rgba(0,0,0,0.28)',
                    pointerEvents: 'none',
                } }), paperFrame.map((r, i) => (_jsx("div", { style: { ...rectStyle(r), background: PAPER, pointerEvents: 'auto' } }, `p${i}`))), _jsx("div", { style: { ...rectStyle(mapWindow), border: '1px solid rgba(62,63,54,0.55)', pointerEvents: 'none' } }), _jsxs("div", { "data-print-overmap": "true", style: { ...rectStyle(mapWindow), pointerEvents: 'none', overflow: 'hidden' }, children: [_jsx(LabelMapOverlay, { viewer: viewer, labels: labels, frame: mapWindow }), _jsx(PrintMapOverlay, { viewer: viewer, scaleSystem: scaleSystem, frame: mapWindow })] }), layout.legend && (legend.hasAny || sourceCredit) && (_jsx("div", { style: {
                    ...rectStyle(layout.legend),
                    pointerEvents: 'auto',
                    background: PAPER,
                    borderLeft: '1px solid rgba(62,63,54,0.35)',
                    overflow: 'hidden',
                }, children: _jsx(PrintLegend, { data: legend, system: scaleSystem, credit: sourceCredit }) })), _jsx("div", { style: {
                    ...rectStyle(titleBlock),
                    pointerEvents: 'auto',
                    borderTop: '2px solid #3e3f36',
                    overflow: 'hidden',
                    background: PAPER,
                }, children: _jsx(PrintTitleBlock, { title: title, subtitle: subtitle, description: description, onDescriptionFit: onDescriptionFit }) })] }));
};
const textFieldStyle = {
    backgroundColor: 'var(--charcoal)',
    color: 'var(--sand)',
    borderColor: 'var(--pewter)',
};
const PrintPanel = ({ orientation, paperKey, cameraMode, title, subtitle, description, descriptionFit, onOrientation, onPaper, onCamera, onTitle, onSubtitle, onDescription, scaleSystem, onScaleSystem, onClose, onExport, exporting, safariBlocked, exportError, legendRows, legendNames, hiddenLegendIds, onToggleLegendHidden, onRenameLegend, groupSimilar, onToggleGroupSimilar, }) => (_jsxs("div", { className: "absolute top-0 left-0 bottom-0 flex flex-col border-r", style: {
        width: PANEL_W,
        backgroundColor: 'var(--forest)',
        borderColor: 'var(--pewter)',
        color: 'var(--sand)',
        pointerEvents: 'auto',
    }, children: [_jsxs("div", { className: "p-4 border-b flex items-center justify-between", style: { borderColor: 'var(--pewter)' }, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Printer, { size: 16, style: { color: 'var(--sand)' } }), _jsx("span", { className: "font-heading text-base font-semibold", children: "Print" })] }), _jsx("button", { onClick: onClose, "aria-label": "Exit print mode", className: "opacity-80 hover:opacity-100", children: _jsx(X, { size: 18 }) })] }), _jsxs("div", { className: "p-4 space-y-4 flex-1 overflow-y-auto", children: [_jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Title" }), _jsx("input", { type: "text", value: title, onChange: (e) => onTitle(e.target.value), placeholder: "Map title", className: "mt-1 w-full px-2 py-1.5 text-sm rounded border", style: textFieldStyle })] }), _jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Subtitle" }), _jsx("input", { type: "text", value: subtitle, onChange: (e) => onSubtitle(e.target.value), placeholder: "Optional subtitle", className: "mt-1 w-full px-2 py-1.5 text-sm rounded border", style: textFieldStyle })] }), _jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Description" }), _jsx("textarea", { value: description, onChange: (e) => onDescription(e.target.value), rows: 3, placeholder: "Add a description for this map\u2026", className: "mt-1 w-full px-2 py-1.5 text-sm rounded border resize-none", style: {
                                backgroundColor: 'var(--charcoal)',
                                color: 'var(--sand)',
                                borderColor: 'var(--pewter)',
                            } }), descriptionFit.shownLines < descriptionFit.totalLines && (_jsxs("p", { className: "mt-1 text-xs", style: { color: '#ef4444' }, children: [descriptionFit.shownLines === 0
                                    ? "The description doesn't fit on this sheet."
                                    : `Only ${descriptionFit.shownLines} of ${descriptionFit.totalLines} lines fit on this sheet.`, ' ', "Shorten it, drop the subtitle, or use a larger paper size."] }))] }), _jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Orientation" }), _jsx(Segmented, { options: [
                                { value: 'landscape', label: 'Landscape' },
                                { value: 'portrait', label: 'Portrait' },
                            ], value: orientation, onChange: (v) => onOrientation(v) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "View" }), _jsx(Segmented, { options: [
                                { value: 'top-down', label: 'Top-down' },
                                { value: '3d', label: '3D' },
                            ], value: cameraMode, onChange: (v) => onCamera(v) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Units" }), _jsx(Segmented, { options: [
                                { value: 'imperial', label: 'Imperial' },
                                { value: 'metric', label: 'Metric' },
                            ], value: scaleSystem, onChange: (v) => onScaleSystem(v) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Paper size" }), _jsx("select", { value: paperKey, onChange: (e) => onPaper(e.target.value), className: "mt-1 w-full px-2 py-1.5 text-sm rounded border", style: {
                                backgroundColor: 'var(--charcoal)',
                                color: 'var(--sand)',
                                borderColor: 'var(--pewter)',
                            }, children: PAPER_SIZES.map((p) => (_jsx("option", { value: p.key, children: p.label }, p.key))) })] }), legendRows.length > 0 && (_jsxs("div", { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-xs", style: { color: 'var(--dew)' }, children: "Legend" }), _jsxs("label", { className: "flex items-center gap-1.5 text-xs cursor-pointer", style: { color: 'var(--sand)' }, children: [_jsx("input", { type: "checkbox", checked: groupSimilar, onChange: onToggleGroupSimilar, style: { accentColor: 'var(--sand)' } }), "Group similar"] })] }), _jsx("div", { className: "mt-2 space-y-1.5", children: legendRows.map((f) => {
                                var _a;
                                const hidden = !!f.id && hiddenLegendIds.has(f.id);
                                const Icon = KIND_ICON[f.kind];
                                return (_jsxs("div", { className: "flex items-center gap-2", style: { opacity: hidden ? 0.45 : 1 }, children: [_jsx("input", { type: "checkbox", checked: !hidden, onChange: () => f.id && onToggleLegendHidden(f.id), style: { accentColor: 'var(--sand)' }, title: hidden ? 'Show in legend' : 'Hide from legend' }), Icon && _jsx(Icon, { size: 15, style: { color: f.color, flexShrink: 0 } }), _jsx("input", { type: "text", 
                                            // Bind to the RAW typed value so it can be cleared to empty;
                                            // f.name is the default and only shows as the placeholder /
                                            // the legend's fallback.
                                            value: (_a = (f.id ? legendNames[f.id] : undefined)) !== null && _a !== void 0 ? _a : f.name, placeholder: f.name, onChange: (e) => f.id && onRenameLegend(f.id, e.target.value), className: "flex-1 min-w-0 px-1.5 py-0.5 text-sm rounded border", style: textFieldStyle })] }, f.id));
                            }) })] }))] }), _jsxs("div", { className: "p-4 border-t space-y-2", style: { borderColor: 'var(--pewter)' }, children: [safariBlocked && (_jsx("p", { className: "text-xs", style: { color: '#e7a3a3' }, children: "A0/A1 export isn\u2019t supported in Safari (browser image-size limit). Use Chrome or Firefox for large-format, or choose A2 or smaller." })), exportError && (_jsx("p", { className: "text-xs", style: { color: '#e7a3a3' }, children: exportError })), _jsx("button", { onClick: onExport, disabled: exporting || safariBlocked, className: "w-full px-3 py-2 text-sm font-semibold rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed", style: { backgroundColor: 'var(--sand)', color: 'var(--forest)' }, children: exporting ? 'Exporting…' : 'Export PDF' }), _jsx("button", { onClick: onClose, disabled: exporting, className: "w-full px-3 py-2 text-sm font-medium rounded-full transition-colors bg-[var(--pewter)] text-[var(--sand)] hover:bg-[var(--sand)] hover:text-[var(--forest)] disabled:opacity-50", children: "Cancel" })] })] }));
const PrintMode = ({ viewer, isOpen, onClose, projectName, loadedArtifacts, isArtifactVisible, selectedProject, artifactControls, visibleFeatures, labels = [], }) => {
    const [orientation, setOrientation] = useState('landscape');
    const [paperKey, setPaperKey] = useState('A2');
    const [cameraMode, setCameraMode] = useState('top-down');
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionFit, setDescriptionFit] = useState({ shownLines: 0, totalLines: 0 });
    const rootRef = useRef(null);
    const [size, setSize] = useState({ w: 0, h: 0 });
    // Bumped whenever the camera moves so the frame-scoped legend re-filters as
    // the user pans/zooms to compose the print.
    const [cameraTick, setCameraTick] = useState(0);
    // Print legend editor state (print-scoped): ids excluded from the legend, and
    // whether to collapse same kind+colour features into one counted row.
    const [hiddenLegendIds, setHiddenLegendIds] = useState(new Set());
    const [groupSimilar, setGroupSimilar] = useState(false);
    // Print-scoped rename overrides, keyed by row id (feature id, or "g:<key>"
    // when grouped — so a collapsed group can be given its own name).
    const [legendNames, setLegendNames] = useState({});
    // Sheet-wide unit system (scale bar + legend) — seeded from the app's unit
    // preference on open, then independently toggleable for this print.
    const unitPrefs = useUnitPreferences();
    const [scaleSystem, setScaleSystem] = useState(unitPrefs.system);
    const [exporting, setExporting] = useState(false);
    const [exportErr, setExportErr] = useState(null);
    const toggleLegendHidden = (id) => setHiddenLegendIds((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const renameLegend = (id, name) => setLegendNames((prev) => ({ ...prev, [id]: name }));
    // Elevation source credit for the legend column. Read from the user-projects
    // list (the selected project here is a thin id/name/bounds object). A project
    // with no recorded source (not processed yet, or a legacy demo) gets no
    // credit rather than a guessed one.
    const { projects } = useUserProjectsContext();
    const sourceCredit = useMemo(() => {
        var _a;
        const id = (_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject);
        const project = id ? projects.find((p) => p.id === id) : undefined;
        const sourceType = resolveDemSourceType(project === null || project === void 0 ? void 0 : project.demSourceType);
        if (!sourceType)
            return null;
        return buildSourceCredit(DEM_SOURCE_CITATIONS[sourceType], project === null || project === void 0 ? void 0 : project.demSourceMetadata, scaleSystem);
    }, [selectedProject, projects, scaleSystem]);
    // Frame-test window. The legend column carves space off the right of the map
    // window, so the test must use the SAME window the user sees — otherwise
    // features sitting in the legend-column strip (a big fraction of the width in
    // portrait) falsely read as in-frame. We reserve the column whenever the
    // project has visible features or a source credit; neither is frame-
    // dependent, so it stays stable across panning — no feedback loop with the
    // legend's own visibility.
    const paper = getPaperSize(paperKey);
    const regionW = Math.max(1, size.w - PANEL_W);
    const reserveLegendCol = (!!visibleFeatures && visibleFeatures.length > 0) || !!sourceCredit;
    const baseTest = computeSheetLayout(regionW, size.h, paper, orientation, { legend: reserveLegendCol });
    const mapWindowTest = baseTest ? shiftX(baseTest.mapWindow, PANEL_W) : null;
    const inFrameFeatures = useMemo(() => filterFeaturesToFrame(viewer, visibleFeatures || [], mapWindowTest), 
    // mapWindowTest is recreated each render; depend on its dimensions + the
    // camera tick so we re-filter on resize, paper/orientation change, and pan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewer, visibleFeatures, isOpen, cameraTick, mapWindowTest === null || mapWindowTest === void 0 ? void 0 : mapWindowTest.x, mapWindowTest === null || mapWindowTest === void 0 ? void 0 : mapWindowTest.y, mapWindowTest === null || mapWindowTest === void 0 ? void 0 : mapWindowTest.width, mapWindowTest === null || mapWindowTest === void 0 ? void 0 : mapWindowTest.height]);
    // editorRows: every legend row (ungrouped or grouped) for the panel checklist.
    // displayedFeatures: the same rows minus the hidden ones — what the legend prints.
    const editorRows = useMemo(() => buildLegendRows(inFrameFeatures, { group: groupSimilar, names: legendNames }), [inFrameFeatures, groupSimilar, legendNames]);
    const displayedFeatures = useMemo(() => presentFeatures(inFrameFeatures, { hiddenIds: hiddenLegendIds, group: groupSimilar, names: legendNames }), [inFrameFeatures, hiddenLegendIds, groupSimilar, legendNames]);
    const legend = usePrintLegend(isOpen, loadedArtifacts, isArtifactVisible, selectedProject, artifactControls, displayedFeatures);
    const showLegendCol = legend.hasAny || !!sourceCredit;
    // Safari caps canvas size, so the largest sheets can't export there. Gate the
    // button + show a clear message rather than letting it fail silently.
    const paperDims = sheetDimsMm(paper, orientation);
    const safariBlocked = isSafari() && exportPixelCount(paperDims.wMm, paperDims.hMm) > SAFARI_MAX_CANVAS_PX;
    const handleExport = async () => {
        if (!viewer || viewer.isDestroyed() || !rootRef.current || exporting)
            return;
        if (safariBlocked)
            return;
        const base = computeSheetLayout(regionW, size.h, paper, orientation, { legend: showLegendCol });
        if (!base)
            return;
        const safeName = (title || 'map').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'map';
        setExportErr(null);
        setExporting(true);
        try {
            await exportPrintPdf({
                viewer,
                rootEl: rootRef.current,
                sheet: shiftX(base.sheet, PANEL_W),
                mapWindow: shiftX(base.mapWindow, PANEL_W),
                paperWmm: paperDims.wMm,
                paperHmm: paperDims.hMm,
                fileName: `${safeName}.pdf`,
            });
        }
        catch (e) {
            console.error('Print export failed', e);
            setExportErr(`Export failed: ${(e === null || e === void 0 ? void 0 : e.message) || e || 'unknown error'}`);
        }
        finally {
            setExporting(false);
        }
    };
    const savedCamera = useRef(null);
    const savedTerrain = useRef(null);
    // Bumped on every terrain change so a slow async world-terrain load that
    // resolves after the user has toggled away (or exited) is ignored.
    const terrainReqRef = useRef(0);
    // Measure the overlay (== map container) so the sheet can be fit beside the panel.
    useLayoutEffect(() => {
        if (!isOpen)
            return;
        const el = rootRef.current;
        if (!el)
            return;
        const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isOpen]);
    // Tick the camera version on every move so the frame-scoped legend re-filters
    // live as the user pans/zooms inside the sheet. percentageChanged is lowered
    // so small pans fire; the rAF coalesces bursts to one update per frame.
    useEffect(() => {
        if (!isOpen || !viewer || viewer.isDestroyed())
            return;
        const cam = viewer.camera;
        const prevPct = cam.percentageChanged;
        cam.percentageChanged = 0.02;
        let raf = 0;
        const onChange = () => {
            if (raf)
                return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                setCameraTick((v) => v + 1);
            });
        };
        const remove = cam.changed.addEventListener(onChange);
        return () => {
            remove();
            if (raf)
                cancelAnimationFrame(raf);
            try {
                cam.percentageChanged = prevPct;
            }
            catch ( /* viewer torn down */_a) { /* viewer torn down */ }
        };
    }, [isOpen, viewer]);
    // Esc exits print mode.
    useEffect(() => {
        if (!isOpen)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);
    // Default the title to the project name each time print mode opens, and reset
    // the print-scoped legend edits so each session starts clean.
    useEffect(() => {
        if (isOpen) {
            setTitle(projectName || '');
            setHiddenLegendIds(new Set());
            setGroupSimilar(false);
            setLegendNames({});
            setScaleSystem(unitPrefs.system);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, projectName]);
    // Save the camera + terrain on enter, restore on exit — print is
    // non-destructive. Declared before the orient/terrain effects so the save
    // captures the pre-print state.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        if (isOpen) {
            const cam = viewer.camera;
            savedCamera.current = {
                position: cam.position.clone(),
                direction: cam.direction.clone(),
                up: cam.up.clone(),
            };
            savedTerrain.current = {
                provider: viewer.terrainProvider,
                depthTest: viewer.scene.globe.depthTestAgainstTerrain,
            };
        }
        else {
            if (savedCamera.current) {
                // Print may have switched to an orthographic frustum (top-down); restore
                // the app's default perspective before reinstating the saved view.
                viewer.camera.switchToPerspectiveFrustum();
                viewer.camera.setView({
                    destination: savedCamera.current.position,
                    orientation: { direction: savedCamera.current.direction, up: savedCamera.current.up },
                });
                savedCamera.current = null;
            }
            if (savedTerrain.current) {
                terrainReqRef.current++; // cancel any in-flight world-terrain load
                viewer.terrainProvider = savedTerrain.current.provider;
                viewer.scene.globe.depthTestAgainstTerrain = savedTerrain.current.depthTest;
                applyTerrainModeToAllVectorLayers(viewer, savedTerrain.current.depthTest);
                savedTerrain.current = null;
            }
            viewer.scene.requestRender();
        }
    }, [isOpen, viewer]);
    // Orient the camera per the View toggle, pivoting around the current view
    // centre: top-down = nadir (north up), 3D = a -45° perspective.
    useEffect(() => {
        if (!isOpen || !viewer || viewer.isDestroyed())
            return;
        const camera = viewer.camera;
        const scene = viewer.scene;
        const canvas = scene.canvas;
        const centerPx = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
        const center = camera.pickEllipsoid(centerPx, Cesium.Ellipsoid.WGS84);
        if (!center)
            return;
        const range = Math.max(1, camera.positionCartographic.height);
        if (cameraMode === 'top-down') {
            // Always start from perspective so switchToOrthographic recomputes the
            // ortho extent from a fresh nadir view. Orthographic = uniform scale, so
            // the scale bar is exact across the whole sheet (true planimetric map).
            camera.switchToPerspectiveFrustum();
            camera.lookAt(center, new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-90), range));
            camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
            camera.switchToOrthographicFrustum();
        }
        else {
            camera.switchToPerspectiveFrustum();
            camera.lookAt(center, new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-45), range));
            camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
        }
        scene.requestRender();
    }, [isOpen, cameraMode, viewer]);
    // 3D auto-enables Cesium World Terrain so the perspective has real relief.
    // Top-down is a flat planimetric map where relief changes nothing on the
    // sheet, so it prints on the ellipsoid and the world-terrain credit drops
    // off the printout. Exit restores the pre-print terrain. Guarded against
    // async races.
    useEffect(() => {
        if (!isOpen || !viewer || viewer.isDestroyed())
            return;
        if (cameraMode === '3d') {
            const req = ++terrainReqRef.current;
            Cesium.createWorldTerrainAsync()
                .then((terrain) => {
                if (viewer.isDestroyed() || req !== terrainReqRef.current)
                    return;
                viewer.terrainProvider = terrain;
                viewer.scene.globe.depthTestAgainstTerrain = true;
                applyTerrainModeToAllVectorLayers(viewer, true);
                viewer.scene.requestRender();
            })
                .catch((e) => console.error('Print: world terrain load failed', e));
        }
        else {
            terrainReqRef.current++; // cancel any in-flight load
            if (viewer.terrainProvider instanceof Cesium.EllipsoidTerrainProvider)
                return;
            viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
            viewer.scene.globe.depthTestAgainstTerrain = false;
            applyTerrainModeToAllVectorLayers(viewer, false);
            viewer.scene.requestRender();
        }
    }, [isOpen, cameraMode, viewer]);
    if (!isOpen)
        return null;
    // Fit the sheet into the region to the right of the docked panel. paper /
    // regionW are computed above (also feed the frame-test window). The real
    // layout reserves the legend column when the (frame-scoped) legend has
    // content or there is a source credit to print.
    const base = computeSheetLayout(regionW, size.h, paper, orientation, { legend: showLegendCol });
    const layout = base
        ? {
            sheet: shiftX(base.sheet, PANEL_W),
            mapWindow: shiftX(base.mapWindow, PANEL_W),
            titleBlock: shiftX(base.titleBlock, PANEL_W),
            legend: base.legend ? shiftX(base.legend, PANEL_W) : undefined,
        }
        : null;
    return (_jsxs("div", { ref: rootRef, className: "absolute inset-0 z-[100]", style: { pointerEvents: 'none' }, children: [layout && (_jsx(SheetOverlay, { layout: layout, containerW: size.w, containerH: size.h, viewer: viewer, title: title, subtitle: subtitle, description: description, scaleSystem: scaleSystem, legend: legend, sourceCredit: sourceCredit, onDescriptionFit: setDescriptionFit, labels: labels })), _jsx(PrintPanel, { orientation: orientation, paperKey: paperKey, cameraMode: cameraMode, title: title, subtitle: subtitle, description: description, descriptionFit: descriptionFit, onOrientation: setOrientation, onPaper: setPaperKey, onCamera: setCameraMode, onTitle: setTitle, onSubtitle: setSubtitle, onDescription: setDescription, scaleSystem: scaleSystem, onScaleSystem: setScaleSystem, onClose: onClose, onExport: handleExport, exporting: exporting, safariBlocked: safariBlocked, exportError: exportErr, legendRows: editorRows, legendNames: legendNames, hiddenLegendIds: hiddenLegendIds, onToggleLegendHidden: toggleLegendHidden, onRenameLegend: renameLegend, groupSimilar: groupSimilar, onToggleGroupSimilar: () => setGroupSimilar((v) => !v) })] }));
};
export default PrintMode;
